import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Button 
          variant="outline" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          ← Back
        </Button>
        
        <div className="bg-gray-800 rounded-lg p-6 md:p-8 text-gray-200">
          <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
          <p className="text-gray-400 mb-6">Effective Date: 8 February 2026</p>
          
          <p className="mb-4">
            Contact Email: info@rydrlinkapp.com
          </p>
          
          <p className="mb-6 text-gray-300">
            RydrLink is committed to protecting your personal data. This Privacy Policy explains how we collect, use, store and share your information in compliance with UK GDPR.
          </p>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">1. Information We Collect</h2>
            <p className="text-gray-300 mb-2">We collect the following types of information:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li><strong>Account Information:</strong> Name, email address, password (encrypted)</li>
              <li><strong>Profile Information:</strong> Bio, experience level, preferred distance, profile picture</li>
              <li><strong>Ride Data:</strong> Rides you create, join, or interact with</li>
              <li><strong>Communications:</strong> Messages you send through the platform</li>
              <li><strong>Location Data:</strong> Postcode (when you provide it), GPS location (only with your explicit consent)</li>
              <li><strong>Usage Data:</strong> How you interact with the platform</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">2. How We Use Your Information</h2>
            <p className="text-gray-300 mb-2">We use your information to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>Provide and improve our services</li>
              <li>Connect you with other cyclists</li>
              <li>Enable ride creation and participation</li>
              <li>Facilitate communication between users</li>
              <li>Send important service notifications</li>
              <li>Ensure platform safety and security</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">3. Location Data & GPS</h2>
            <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4 mb-4">
              <p className="text-purple-200 font-medium">
                Important: We will ONLY access your GPS location when you explicitly grant permission. GPS access is never automatic or assumed.
              </p>
            </div>
            <p className="text-gray-300 mb-2">When you grant GPS permission, we may use it to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>Show rides near your current location</li>
              <li>Enable future safety features (e.g., LiveLink SOS)</li>
            </ul>
            <p className="text-gray-300 mt-2">You can revoke GPS permission at any time through your browser or device settings.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">4. Legal Basis for Processing</h2>
            <p className="text-gray-300 mb-2">We process your data based on:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li><strong>Contract:</strong> To provide you with our services</li>
              <li><strong>Consent:</strong> For optional features like GPS location</li>
              <li><strong>Legitimate Interest:</strong> To improve our platform and ensure safety</li>
              <li><strong>Legal Obligation:</strong> To comply with applicable laws</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">5. Data Sharing</h2>
            <p className="text-gray-300 mb-2">We may share your information with:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li><strong>Other Users:</strong> Your profile information and ride activity is visible to other users</li>
              <li><strong>Service Providers:</strong> Third parties who help us operate the platform (e.g., hosting, authentication)</li>
              <li><strong>Legal Authorities:</strong> When required by law or to protect safety</li>
            </ul>
            <p className="text-gray-300 mt-2">We do not sell your personal data to third parties.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">6. Data Retention</h2>
            <p className="text-gray-300 mb-2">We retain your data for as long as your account is active or as needed to provide services.</p>
            <p className="text-gray-300">When you delete your account, we will delete your personal data within 30 days, except where we need to retain it for legal purposes.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">7. Your Rights (UK GDPR)</h2>
            <p className="text-gray-300 mb-2">You have the right to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Correct inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Portability:</strong> Receive your data in a portable format</li>
              <li><strong>Object:</strong> Object to certain processing of your data</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-gray-300 mt-2">To exercise these rights, contact us at info@rydrlinkapp.com</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">8. Data Security</h2>
            <p className="text-gray-300 mb-2">We implement appropriate security measures to protect your data, including:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>Encryption of sensitive data</li>
              <li>Secure authentication via Firebase</li>
              <li>Regular security reviews</li>
            </ul>
            <p className="text-gray-300 mt-2">However, no system is 100% secure. We encourage you to use strong passwords and protect your account.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">9. Cookies</h2>
            <p className="text-gray-300 mb-2">We use essential cookies to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>Keep you logged in</li>
              <li>Remember your preferences</li>
              <li>Ensure platform functionality</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">10. Children's Privacy</h2>
            <p className="text-gray-300">RydrLink is not intended for users under 18 years old. We do not knowingly collect data from children.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">11. Changes to This Policy</h2>
            <p className="text-gray-300 mb-2">We may update this Privacy Policy from time to time.</p>
            <p className="text-gray-300">Significant changes will be communicated through the platform or via email.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">12. Contact Us</h2>
            <p className="text-gray-300 mb-2">If you have questions about this Privacy Policy or your data, contact us at:</p>
            <p className="text-purple-400">info@rydrlinkapp.com</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-3">13. Supervisory Authority</h2>
            <p className="text-gray-300 mb-2">You have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you believe your data has been mishandled:</p>
            <p className="text-gray-400">ico.org.uk</p>
          </section>
        </div>
      </div>
    </div>
  );
}
